import { ReactElement, useMemo, useCallback, useState } from 'react';
import { Typography, Stack } from '@mui/material';

import { MultipleLinearProgressProps } from './types';
import { getTotalItems } from './utils';
import MultipleLinearProgressLegendLabel from './LegendLabel';
import {
  MultipleLinearProgresContainer,
  MultipleLinearProgresContent,
  MultipleLinearProgresTooltip,
  MultipleLinearProgresBar,
  MultipleLinearProgresSegment,
  MultipleLinearProgresLegendContainer,
  MultipleLinearProgresLegendItem,
  MultipleLinearProgresLegendDot,
  MultipleLinearProgresLegendLabelContainer,
} from './styles';

const MultipleLinearProgress = ({
  items,
  max,
  size = 'medium',
  showTotal = true,
  showLegend = true,
  enabledTooltip = false,
  borderRadiusType = ['left', 'right'],
}: MultipleLinearProgressProps): ReactElement => {
  const total = useMemo(() => {
    if (max) return max;

    return getTotalItems(items);
  }, [max, items]);

  const getCurrentProgressVal = useCallback(
    (value: number) => (value / total) * 100,
    [total],
  );

  const [showTooltip, setShowTooltip] = useState(false);

  const handleOpenTooltip = useCallback(() => {
    if (enabledTooltip) {
      setShowTooltip(true);
    }
  }, [enabledTooltip]);

  const tooltipTitle = useMemo(
    () => (
      <Stack spacing={1} direction="row">
        {items.map((item, itemId) => (
          <Stack
            key={`multiple-linear-tooltip-item-${itemId}`}
            spacing={1}
            direction="row"
            alignItems="center"
          >
            <MultipleLinearProgresLegendDot
              bgcolor={item.color}
              sx={{ width: 10, height: 10 }}
            />

            <Stack spacing={0.5} direction="row">
              <Typography variant="inherit" fontWeight="bold">
                {item.value}
              </Typography>

              <Typography variant="inherit">{item.label}</Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
    ),
    [items],
  );

  return (
    <MultipleLinearProgresContainer>
      <MultipleLinearProgresContent>
        <MultipleLinearProgresTooltip
          open={showTooltip}
          onOpen={handleOpenTooltip}
          onClose={() => setShowTooltip(false)}
          title={tooltipTitle}
          arrow
          placement="top"
        >
          <MultipleLinearProgresBar
            size={size}
            borderRadiusType={borderRadiusType}
          >
            {items.map((item, itemIndex) => (
              <MultipleLinearProgresSegment
                key={`multiple-linear-progress-segment-${itemIndex}`}
                width={`${getCurrentProgressVal(item.value)}%`}
                bgcolor={item.color}
              />
            ))}
          </MultipleLinearProgresBar>
        </MultipleLinearProgresTooltip>

        {showTotal && (
          <Typography variant="body2" fontWeight="bold">
            {total}
          </Typography>
        )}
      </MultipleLinearProgresContent>

      {showLegend && items.length > 0 && (
        <MultipleLinearProgresLegendContainer>
          {items.map((item, itemId) => (
            <MultipleLinearProgresLegendItem
              key={`multiple-linear-legend-item-${itemId}`}
            >
              {item.customDot && item.customDot}

              {!item.customDot && (
                <MultipleLinearProgresLegendDot bgcolor={item.color} />
              )}

              <MultipleLinearProgresLegendLabelContainer>
                <Typography variant="body2" fontWeight="bold">
                  {item.value}
                </Typography>

                <MultipleLinearProgressLegendLabel
                  label={item.label}
                  onClick={item.onClick}
                />
              </MultipleLinearProgresLegendLabelContainer>
            </MultipleLinearProgresLegendItem>
          ))}
        </MultipleLinearProgresLegendContainer>
      )}
    </MultipleLinearProgresContainer>
  );
};

export default MultipleLinearProgress;
